import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/shared/services/api/client'

/**
 * 后端 `GET /api/version` 的**原始响应**结构
 *
 * 字段名是后端那套 snake_case，与本文件对外的 `AppVersionInfo`（camelCase）刻意分开：
 * 直接用 `AppVersionInfo` 当 useQuery 的泛型，`data.started_at` 会被 TS 判成不存在的属性
 * （属性名是 `startedAt`），而运行时拿到的确实是后端的原始字段——类型与事实不符。
 */
interface AppVersionApiResponse {
  version?: string
  branch?: string
  commit?: string
  started_at?: string
}

/** 对外暴露的版本信息（前端命名风格，字段已归一化，不会出现空串以外的缺失态） */
export interface AppVersionInfo {
  /** 整体版本号，如 v1.0.0-20261012194855；缺失时为 unknown */
  version: string
  /** 部署所用分支，如 dev / main；缺失时为空字符串 */
  branch: string
  /** 部署 commit 短哈希；缺失时为空字符串 */
  commit: string
  /** 服务启动时间，ISO 8601 带时区偏移；缺失时为空字符串 */
  startedAt: string
}

/** 接口不可用时的兜底版本号（构建时注入，见 frontend/.env.example） */
const FALLBACK_VERSION = import.meta.env.VITE_APP_VERSION || 'unknown'

/**
 * 获取当前部署的版本信息
 *
 * 版本号由部署流水线写入 apps/.env，经后端 /api/version 暴露；
 * startedAt 是后端进程启动时刻（后端进程内变量，见 apps/main_di.py）。
 * 一次部署内版本不会变化，因此不轮询、不在窗口聚焦时重取；
 * 接口故障时静默降级为构建时兜底值，不弹错误提示、不影响页面其它功能。
 * 详见 docs/version_management_design.md
 */
export const useAppVersion = (): AppVersionInfo => {
  const { data } = useQuery<AppVersionApiResponse>({
    queryKey: ['app-version'],
    queryFn: async () => {
      const response = await apiClient.get<AppVersionApiResponse>('/api/version')
      return response.data
    },
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 0,
  })

  return {
    version: data?.version?.trim() || FALLBACK_VERSION,
    branch: data?.branch?.trim() || '',
    commit: data?.commit?.trim() || '',
    startedAt: data?.started_at?.trim() || '',
  }
}

/** 悬浮提示：分支 / commit 补充信息，两者都为空时返回 undefined（不显示 title） */
export const buildVersionTooltip = (info: AppVersionInfo): string | undefined => {
  const parts: string[] = []
  if (info.branch) parts.push(`分支 ${info.branch}`)
  if (info.commit) parts.push(`commit ${info.commit}`)
  return parts.length > 0 ? parts.join(' · ') : undefined
}

/**
 * 把后端返回的 ISO 8601 启动时间格式化成「2026-10-13 14:24:41」
 *
 * 用本地时区展示：后端给的是带 offset 的时间点，这里只决定怎么显示。
 * 解析失败（老后端没这个字段、值被改坏）时返回空字符串，由调用方决定不渲染，
 * **不返回原文**——把 ISO 串直接摆到界面上比不显示更难看。
 */
export const formatStartedAt = (iso: string): string => {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ` +
    `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
  )
}
